import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, ShoppingBag } from "lucide-react";

const money = (p) => (p == null || p === "" ? null : String(p).includes("€") ? p : `${p} €`);

export function MenuViewer({ items, start = 0, onClose }) {
  const ref = useRef(null);
  const [idx, setIdx] = useState(start);
  useEffect(() => {
    const el = ref.current;
    if (el) el.scrollLeft = start * el.clientWidth;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [start, onClose]);
  const onScroll = () => { const el = ref.current; if (el) setIdx(Math.round(el.scrollLeft / el.clientWidth)); };
  const go = (d) => { const el = ref.current; if (el) el.scrollTo({ left: (idx + d) * el.clientWidth, behavior: "smooth" }); };

  return createPortal(
    <div data-testid="menu-viewer" className="fixed inset-0 z-[100] bg-slate-950/95 flex flex-col gs-fade">
      <div className="flex items-center justify-between px-4 pt-4 pb-2 text-white">
        <span data-testid="menu-viewer-count" className="text-sm font-semibold text-white/70">{idx + 1} / {items.length}</span>
        <button data-testid="menu-viewer-close" onClick={onClose} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"><X className="w-5 h-5" /></button>
      </div>
      <div ref={ref} onScroll={onScroll} className="flex-1 flex overflow-x-auto snap-x snap-mandatory no-scrollbar">
        {items.map((it, i) => (
          <div key={i} data-testid={`menu-viewer-item-${i}`} className="w-full h-full shrink-0 snap-center flex flex-col justify-center px-5 pb-8">
            {it.image
              ? <img src={it.image} alt={it.name} draggable={false} className="w-full max-h-[60vh] object-contain rounded-3xl" />
              : <div className="w-full h-64 rounded-3xl bg-white/5 flex items-center justify-center"><ShoppingBag className="w-12 h-12 text-white/30" /></div>}
            <div className="mt-5 text-white">
              {it.cat && <p className="text-xs uppercase tracking-wider text-emerald-400 font-bold">{it.cat}</p>}
              {it.name && <p className="text-2xl font-extrabold mt-1 break-words">{it.name}</p>}
              {money(it.price) && <p data-testid={`menu-viewer-price-${i}`} className="text-xl font-extrabold text-emerald-400 mt-1">{money(it.price)}</p>}
              {(it.desc || it.description) && <p className="text-sm text-white/70 mt-2 break-words">{it.desc || it.description}</p>}
            </div>
          </div>
        ))}
      </div>
      {idx > 0 && <button data-testid="menu-viewer-prev" onClick={() => go(-1)} className="hidden md:flex absolute left-4 top-1/2 w-11 h-11 rounded-full bg-white/10 text-white items-center justify-center"><ChevronLeft /></button>}
      {idx < items.length - 1 && <button data-testid="menu-viewer-next" onClick={() => go(1)} className="hidden md:flex absolute right-4 top-1/2 w-11 h-11 rounded-full bg-white/10 text-white items-center justify-center"><ChevronRight /></button>}
    </div>,
    document.body
  );
}
